'use client';

import { useCallback, useMemo, useRef, useEffect, useState } from 'react';
import type { DrawingDataPoint, DrawingBoundingBox } from './types';

interface PlotlyAxisLayout {
  range?: [number | string, number | string];
  _length?: number;
  _offset?: number;
  l2p?: (v: number) => number; // Linear to pixel
  p2l?: (p: number) => number; // Pixel to linear
  d2p?: (d: number | string | Date) => number; // Data to pixel
  p2d?: (p: number) => number | string | Date; // Pixel to data
  d2l?: (d: number | string | Date) => number; // Data to linear
  l2d?: (l: number) => number | string | Date; // Linear to data
  type?: 'date' | 'linear' | 'log' | 'category';
}

interface PlotlyLayout {
  xaxis?: PlotlyAxisLayout;
  yaxis?: PlotlyAxisLayout;
  margin?: { l: number; r: number; t: number; b: number };
  width?: number;
  height?: number;
}

interface PlotlyElement extends HTMLElement {
  _fullLayout?: PlotlyLayout;
  _fullData?: any[];
}

export interface CoordinateTransformOptions {
  xAxisType?: 'date' | 'numeric' | 'category';
}

export interface CoordinateTransformResult {
  // Transform functions
  pixelToData: (pixelX: number, pixelY: number) => DrawingDataPoint | null;
  dataToPixel: (dataX: number | Date, dataY: number) => { x: number; y: number } | null;

  // Get chart bounds in data coordinates
  getDataBounds: () => DrawingBoundingBox | null;

  // Get plot area dimensions in pixels
  getPlotArea: () => { x: number; y: number; width: number; height: number } | null;

  // Check if a pixel point is within the plot area
  isWithinPlotArea: (pixelX: number, pixelY: number) => boolean;

  // Re-calculate on layout change
  refresh: () => void;

  // Whether transforms are ready (Plotly has rendered)
  isReady: boolean;
}

/**
 * Hook to transform coordinates between pixel space and data space
 * for the Plotly chart.
 */
export function useCoordinateTransform(
  plotRef: React.RefObject<{ el: PlotlyElement | null } | null>,
  options: CoordinateTransformOptions = {}
): CoordinateTransformResult {
  const { xAxisType = 'numeric' } = options;

  // Track layout state for re-renders
  const [layoutVersion, setLayoutVersion] = useState(0);
  const cachedLayoutRef = useRef<PlotlyLayout | null>(null);

  // Refresh function to force recalculation
  const refresh = useCallback(() => {
    setLayoutVersion((v) => v + 1);
  }, []);

  // Get the Plotly element and layout
  const getPlotlyLayout = useCallback((): PlotlyLayout | null => {
    const plotElement = plotRef.current?.el;
    if (!plotElement?._fullLayout) return null;
    return plotElement._fullLayout;
  }, [plotRef]);

  // Check if transforms are ready
  const isReady = useMemo(() => {
    const layout = getPlotlyLayout();
    return !!(layout?.xaxis && layout?.yaxis);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getPlotlyLayout, layoutVersion]);

  // Get plot area dimensions
  const getPlotArea = useCallback(() => {
    const layout = getPlotlyLayout();
    if (!layout?.xaxis || !layout?.yaxis || !layout.margin) return null;

    const { margin } = layout;
    const xaxis = layout.xaxis;
    const yaxis = layout.yaxis;

    // Plotly stores the axis length and offset
    const plotWidth = xaxis._length ?? (layout.width ?? 0) - margin.l - margin.r;
    const plotHeight = yaxis._length ?? (layout.height ?? 0) - margin.t - margin.b;

    return {
      x: margin.l,
      y: margin.t,
      width: plotWidth,
      height: plotHeight,
    };
  }, [getPlotlyLayout]);

  // Check if point is within plot area
  const isWithinPlotArea = useCallback(
    (pixelX: number, pixelY: number): boolean => {
      const plotArea = getPlotArea();
      if (!plotArea) return false;

      return (
        pixelX >= plotArea.x &&
        pixelX <= plotArea.x + plotArea.width &&
        pixelY >= plotArea.y &&
        pixelY <= plotArea.y + plotArea.height
      );
    },
    [getPlotArea]
  );

  // Pixel to data coordinate transform
  const pixelToData = useCallback(
    (pixelX: number, pixelY: number): DrawingDataPoint | null => {
      const layout = getPlotlyLayout();
      if (!layout?.xaxis || !layout?.yaxis) return null;

      const plotArea = getPlotArea();
      if (!plotArea) return null;

      const { xaxis, yaxis } = layout;

      try {
        // Calculate relative position within plot area (0-1)
        const relX = (pixelX - plotArea.x) / plotArea.width;
        const relY = (pixelY - plotArea.y) / plotArea.height;

        // Get axis ranges
        const xRange = xaxis.range;
        const yRange = yaxis.range;

        if (!xRange || !yRange) return null;

        // Convert to data coordinates
        let dataX: number | Date;
        let dataY: number;

        // X-axis conversion
        if (xAxisType === 'date' && xaxis.type === 'date') {
          // For date axes, range values are milliseconds
          const xMin = typeof xRange[0] === 'string' ? new Date(xRange[0]).getTime() : Number(xRange[0]);
          const xMax = typeof xRange[1] === 'string' ? new Date(xRange[1]).getTime() : Number(xRange[1]);
          const xValue = xMin + relX * (xMax - xMin);
          dataX = new Date(xValue);
        } else {
          // Numeric axis
          const xMin = Number(xRange[0]);
          const xMax = Number(xRange[1]);
          dataX = xMin + relX * (xMax - xMin);
        }

        // Y-axis conversion (inverted - top is max, bottom is min)
        const yMin = Number(yRange[0]);
        const yMax = Number(yRange[1]);
        dataY = yMax - relY * (yMax - yMin);

        return { x: dataX, y: dataY };
      } catch {
        return null;
      }
    },
    [getPlotlyLayout, getPlotArea, xAxisType]
  );

  // Data to pixel coordinate transform
  const dataToPixel = useCallback(
    (dataX: number | Date, dataY: number): { x: number; y: number } | null => {
      const layout = getPlotlyLayout();
      if (!layout?.xaxis || !layout?.yaxis) return null;

      const plotArea = getPlotArea();
      if (!plotArea) return null;

      const { xaxis, yaxis } = layout;

      try {
        const xRange = xaxis.range;
        const yRange = yaxis.range;

        if (!xRange || !yRange) return null;

        // X-axis conversion
        let relX: number;
        if (dataX instanceof Date) {
          const xMin = typeof xRange[0] === 'string' ? new Date(xRange[0]).getTime() : Number(xRange[0]);
          const xMax = typeof xRange[1] === 'string' ? new Date(xRange[1]).getTime() : Number(xRange[1]);
          relX = (dataX.getTime() - xMin) / (xMax - xMin);
        } else {
          const xMin = Number(xRange[0]);
          const xMax = Number(xRange[1]);
          relX = (dataX - xMin) / (xMax - xMin);
        }

        // Y-axis conversion (inverted)
        const yMin = Number(yRange[0]);
        const yMax = Number(yRange[1]);
        const relY = (yMax - dataY) / (yMax - yMin);

        // Convert to pixels
        const pixelX = plotArea.x + relX * plotArea.width;
        const pixelY = plotArea.y + relY * plotArea.height;

        return { x: pixelX, y: pixelY };
      } catch {
        return null;
      }
    },
    [getPlotlyLayout, getPlotArea]
  );

  // Get data bounds
  const getDataBounds = useCallback((): DrawingBoundingBox | null => {
    const layout = getPlotlyLayout();
    if (!layout?.xaxis?.range || !layout?.yaxis?.range) return null;

    const { xaxis, yaxis } = layout;
    const xRange = xaxis.range!;
    const yRange = yaxis.range!;

    return {
      xMin: Number(xRange[0]),
      xMax: Number(xRange[1]),
      yMin: Number(yRange[0]),
      yMax: Number(yRange[1]),
    };
  }, [getPlotlyLayout]);

  // Listen for Plotly relayout events
  useEffect(() => {
    const plotElement = plotRef.current?.el;
    if (!plotElement) return;

    const handleRelayout = () => {
      refresh();
    };

    // Plotly emits 'plotly_relayout' after layout changes
    plotElement.addEventListener('plotly_relayout', handleRelayout);
    plotElement.addEventListener('plotly_afterplot', handleRelayout);

    return () => {
      plotElement.removeEventListener('plotly_relayout', handleRelayout);
      plotElement.removeEventListener('plotly_afterplot', handleRelayout);
    };
  }, [plotRef, refresh]);

  return {
    pixelToData,
    dataToPixel,
    getDataBounds,
    getPlotArea,
    isWithinPlotArea,
    refresh,
    isReady,
  };
}

/**
 * Calculate bounding box from an array of points
 */
export function calculateBounds(points: DrawingDataPoint[]): DrawingBoundingBox {
  if (points.length === 0) {
    return { xMin: 0, xMax: 0, yMin: 0, yMax: 0 };
  }

  const xValues = points.map((p) => (p.x instanceof Date ? p.x.getTime() : p.x));
  const yValues = points.map((p) => p.y);

  return {
    xMin: Math.min(...xValues),
    xMax: Math.max(...xValues),
    yMin: Math.min(...yValues),
    yMax: Math.max(...yValues),
  };
}

/**
 * Check if a point is within a bounding box (with optional padding)
 */
export function isPointInBounds(
  point: DrawingDataPoint,
  bounds: DrawingBoundingBox,
  padding = 0
): boolean {
  const x = point.x instanceof Date ? point.x.getTime() : point.x;
  return (
    x >= bounds.xMin - padding &&
    x <= bounds.xMax + padding &&
    point.y >= bounds.yMin - padding &&
    point.y <= bounds.yMax + padding
  );
}
